import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectFunction, Router } from '@angular/router';

import { ClientConfigProductName, LastKnownProductService, Page, ProductService } from '@frontend/vanilla/core';

import { SamePathAppVersion } from './app.version.enum';
import { HostAppClientConfigService } from './clientconfig.service';
import { ProductStylesService } from './product-styles.service';
import { HostAppConfig } from './provide';

export const lastKnownProductRedirectFunction: RedirectFunction = () => {
    const lastKnownProductService = inject(LastKnownProductService);
    const config = inject(HostAppConfig);
    const router = inject(Router);
    const page = inject(Page);
    const lkp = lastKnownProductService.get();

    return router.createUrlTree([
        page.lang,
        (lkp.name === lastKnownProductService.UNKNOWN ? config.defaultPath : config.productPathMap[lkp.name]).toLowerCase(),
    ]);
};

export const hostAppMatchAppVersionGuard = (app: ClientConfigProductName, version: SamePathAppVersion) => {
    const config = inject(HostAppConfig);
    return config.samePathAppVersions[app] === version;
};

export const clientConfigAppGuard = async (route: ActivatedRouteSnapshot) => {
    const hostAppRouteService = inject(HostAppClientConfigService);
    await hostAppRouteService.tryLoad(route.data['product']);
    return true;
};

export const productStyleBundleGuard = async (route: ActivatedRouteSnapshot) => {
    const productStylesService = inject(ProductStylesService);
    await productStylesService.tryLoad(route.data['product']);

    return true;
};

export const matchAppVersionProductLoadGuard = async (app: ClientConfigProductName, version: SamePathAppVersion, route: ActivatedRouteSnapshot) => {
    const appMatch = hostAppMatchAppVersionGuard(app, version);

    if (!appMatch) {
        return false;
    }

    const [stylesLoaded, configLoaded] = await Promise.all([productStyleBundleGuard(route), clientConfigAppGuard(route)]);

    return stylesLoaded && configLoaded;
};

export const activateHostProductGuard = () => {
    inject(ProductService).setDefaultActive();
};

export const allowedProductGuard = async (route: ActivatedRouteSnapshot) => isAllowedProduct(route.data['product']);

export const isAllowedProduct = (product: ClientConfigProductName) =>
    inject(HostAppConfig).configuredProducts.some((item) => item.toLowerCase() === product);

export const isSportsVersionMatch = (version: SamePathAppVersion) => () => hostAppMatchAppVersionGuard(ClientConfigProductName.SPORTS, version);

export const isActiveProductGuard = (product: ClientConfigProductName) => () => inject(ProductService).current.name === product;
